import {
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Text,
  IconButton,
  useDisclosure,
  Button,
  Box,
  Textarea,
  InputGroup,
  InputRightAddon,
} from '@chakra-ui/react';
import {
  ColorData,
  CompositionMaterial,
  CreateCompositionMaterialRequest,
} from '@texas/api/endpoints/compositionApi';
import { QualityListItem } from '@texas/api/endpoints/metadata/qualitiesApi';
import { TreatmentListItem } from '@texas/api/endpoints/metadata/treatmentsApi';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { Icons } from '@texas/components/shared/Icons';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { ReactSelectOption, ReactSelectOptionWithDesc } from '@texas/types';
import { useRef, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ColorPicker, GetColorWithType } from '../../colorPicker/ColorPicker';
import { setValueAsDecimalExtender } from '../../../article/variant/sales/shared';
import { AutoGrowTextareaContainer } from '@texas/components/shared/form/AutoGrowTextareaContainer';
import { LoadingOverlayV2 } from '@texas/components/shared/LoadingOverlayV2';
import { ColorBox } from '../../colorPicker/ColorBox';
import React from 'react';
import { PopoverOptionComponent } from '../../../article/variant/compositions/shared/composition/PopoverOptionComponent';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { GroupBase } from 'react-select';
import { SimpleNote } from '../../SimpleNote';
import { useCompositionData } from './useCompositionData';
import { formatLabel } from './shared';

export type MaterialFormType = 'controller' | 'cell-controller' | 'vdc';

interface Props extends SharedDisclosureProps {
  loading: boolean;
  compositionMaterial?: CompositionMaterial | null;
  subTitle?: string;
  productGroupId: number | null;
  onFormSubmit: (data: CreateCompositionMaterialRequest) => void;
  formType?: MaterialFormType;
  isReady: boolean;
  groupHasAnyBasedOn: boolean;
  vdcTemplate: boolean;
}

export function CompositionMaterialForm({
  loading,
  compositionMaterial,
  productGroupId,
  isOpen,
  onClose,
  onFormSubmit,
  subTitle,
  formType,
  isReady,
  vdcTemplate,
}: Props) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateCompositionMaterialRequest>({
    defaultValues: {
      materialId: null,
      note: '',
      techniqueIds: [],
      treatmentIds: [],
      weight: 0,
      colors: [],
      qualities: [],
    },
  });

  const { t } = useTranslation();

  const {
    onOpen: onSelectColorOpen,
    onClose: onSelectColorClose,
    isOpen: isSelectColorOpen,
  } = useDisclosure();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'qualities',
  });

  const [qualityIds, setQualityIds] = useState<number[]>([]);
  const ref = useRef(null);

  useEffect(() => {
    if (!compositionMaterial) return;
    setQualityIds(compositionMaterial.qualities.map((x) => x.id));
    const colors = compositionMaterial.colors.map<ColorData>((x) => ({
      ...(x.colorType == null
        ? GetColorWithType(x)
        : { hex: x.hex, code: x.code, colorType: x.colorType, name: x.name }),
      description: x.description,
    }));

    reset({
      materialId: compositionMaterial.material.id,
      note: compositionMaterial.note,
      techniqueIds: compositionMaterial.techniques.map((x) => x.id),
      treatmentIds: compositionMaterial.treatments.map((x) => x.id),
      weight: compositionMaterial.weight,
      colors: colors,
      qualities: compositionMaterial.qualities.map((x) => ({
        id: x.id,
        value: x.value,
      })),
    });
  }, [compositionMaterial, reset]);

  const onSubmit = (data: CreateCompositionMaterialRequest) => {
    onFormSubmit(data);
    reset();
    setQualityIds([]);
    onClose();
  };

  const {
    qualities,
    treatments,
    techniques,
    materials,
    materialsLoading,
    techniquesLoading,
    treatmentsLoading,
    qualitiesLoading,
  } = useCompositionData({ vdcSupport: vdcTemplate });

  return (
    <>
      <ColorPicker
        onSelect={(e) => {
          setValue('colors', e);
          onSelectColorClose();
        }}
        onClose={onSelectColorClose}
        isOpen={isSelectColorOpen}
        addedColors={watch('colors')}
        revertReadyWarning={false}
        isReady={isReady}
      />
      <Modal
        size="xl"
        isCentered={true}
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent ref={ref}>
          <ModalHeader>
            <Flex flexDir="column">
              {t('materials.compositionMaterial')}
              {subTitle && <Text fontSize="md">{subTitle}</Text>}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="90dvh" overflow="auto">
            <LoadingOverlayV2
              loaded={!loading}
              label={t('materials.loadingMaterial')}
            />
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <Flex flexDir="column" gap={2}>
                {formType == 'cell-controller' ? (
                  <SimpleNote>
                    {t('composition.cellControllerDescription')}
                  </SimpleNote>
                ) : (
                  <>
                    <FormControl isInvalid={!!errors.materialId}>
                      <TexasFormLabel>{t('general.materials')}</TexasFormLabel>
                      <Controller
                        name="materialId"
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: t('materials.materialRequired'),
                          },
                        }}
                        render={({ field }) => (
                          <TexasSelect
                            isLoading={materialsLoading}
                            value={{
                              label: formatLabel(
                                materials?.find((p) => p.id === field.value),
                              ),
                              value: field.value,
                            }}
                            onChange={(e) => field.onChange(e?.value)}
                            options={
                              materials?.map<ReactSelectOption>((x) => ({
                                value: x.id,
                                label: formatLabel(x),
                              })) ?? []
                            }
                            closeMenuOnSelect={true}
                          />
                        )}
                      />
                      <ErrorLabel text={errors.materialId?.message} />
                    </FormControl>
                    <FormControl>
                      <TexasFormLabel>
                        {t('configuration.techniques')}
                      </TexasFormLabel>
                      <Controller
                        name="techniqueIds"
                        control={control}
                        render={({ field }) => (
                          <TexasSelect
                            {...field}
                            isLoading={techniquesLoading}
                            isClearable={true}
                            value={field.value?.map<ReactSelectOptionWithDesc>(
                              (x) => ({
                                label:
                                  techniques?.find((p) => p.id === x)?.value ??
                                  '',
                                value: x,
                                description:
                                  techniques?.find((p) => p.id === x)
                                    ?.description ?? undefined,
                              }),
                            )}
                            isMulti={true}
                            onChange={(e) =>
                              field.onChange(e.map((x) => x.value))
                            }
                            options={
                              techniques?.map<ReactSelectOptionWithDesc>(
                                (x) => ({
                                  value: x.id,
                                  label: x.value,
                                  description: x.description ?? undefined,
                                  ref: ref,
                                }),
                              ) ?? []
                            }
                            closeMenuOnSelect={false}
                            components={{ Option: PopoverOptionComponent }}
                          />
                        )}
                      />
                    </FormControl>
                    <FormControl>
                      <TexasFormLabel>
                        {t('configuration.treatments')}
                      </TexasFormLabel>
                      <Controller
                        name="treatmentIds"
                        control={control}
                        render={({ field }) => (
                          <TexasSelect
                            {...field}
                            isLoading={treatmentsLoading}
                            isClearable={true}
                            value={field.value?.map<ReactSelectOptionWithDesc>(
                              (x) => ({
                                label:
                                  treatments?.find((p) => p.id === x)?.name ??
                                  '',
                                value: x,
                                description: treatments?.find((p) => p.id === x)
                                  ?.description,
                              }),
                            )}
                            isMulti={true}
                            onChange={(e) =>
                              field.onChange(e.map((x) => x.value))
                            }
                            options={GetGroupedTreatmentOptions(
                              treatments,
                              productGroupId,
                              ref,
                            )}
                            closeMenuOnSelect={false}
                            components={{ Option: PopoverOptionComponent }}
                          />
                        )}
                      />
                    </FormControl>
                    <Flex flexDir="column">
                      <TexasFormLabel>
                        {t('configuration.qualities')}
                      </TexasFormLabel>
                      <FormControl
                        bg="texas.bg.800"
                        _light={{ bg: 'gray.10' }}
                        p={2}
                        borderRadius={6}
                      >
                        <TexasSelect
                          isLoading={qualitiesLoading}
                          isClearable={false}
                          isMulti={true}
                          value={qualityIds.map<ReactSelectOption>((x) => ({
                            label:
                              qualities?.find((p) => p.id === x)?.name ?? '',
                            value: x,
                          }))}
                          onChange={(_, meta) => {
                            if (meta.action === 'select-option') {
                              setQualityIds((s) => [...s, meta.option?.value]);
                              append({ id: meta.option?.value, value: '' });
                              return;
                            }

                            setQualityIds((s) =>
                              s.filter((x) => x !== meta.removedValue?.value),
                            );
                            remove(
                              fields.findIndex(
                                (x) => x.id === meta.removedValue?.value,
                              ),
                            );
                          }}
                          options={GetGroupedQualitiesOptions(
                            qualities,
                            productGroupId,
                          )}
                          closeMenuOnSelect={false}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          menuPortalTarget={document.body}
                        />
                        <Box pt={2}>
                          {fields.map((x, i) => {
                            const quality = qualities?.find(
                              (q) => q.id === qualityIds[i],
                            );
                            return (
                              <React.Fragment key={x.id}>
                                <Flex alignItems="center" gap={2} pt={2}>
                                  <Text w="xl">{quality?.name}</Text>
                                  {quality?.inputField && (
                                    <FormControl
                                      isInvalid={!!errors.qualities?.[i]?.value}
                                    >
                                      <Input
                                        {...register(
                                          `qualities.${i}.value` as const,
                                          {
                                            maxLength: 50,
                                          },
                                        )}
                                        variant="outline"
                                      />
                                    </FormControl>
                                  )}
                                  <IconButton
                                    ml="auto"
                                    size="sm"
                                    variant="ghost"
                                    icon={<Icons.Close />}
                                    onClick={() => {
                                      setQualityIds((x) =>
                                        x.filter((q) => q !== quality?.id),
                                      );
                                      remove(i);
                                    }}
                                    aria-label={t('general.remove')}
                                  />
                                </Flex>
                                <ErrorLabel
                                  text={errors.qualities?.[i]?.value?.message}
                                />
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      </FormControl>
                    </Flex>
                  </>
                )}
                {formType !== 'controller' && (
                  <Flex direction="column">
                    <TexasFormLabel>{t('general.colors')}</TexasFormLabel>
                    <FormControl
                      bg="texas.bg.800"
                      _light={{ bg: 'gray.10' }}
                      p={2}
                      borderRadius={6}
                    >
                      <Flex flexDir="column" gap={2}>
                        <Button
                          variant="texas-light"
                          onClick={() => onSelectColorOpen()}
                          w="full"
                        >
                          {t('colors.selectColors')}
                        </Button>
                        {watch('colors').map((x) => (
                          <ColorBox
                            color={x}
                            key={`${x.code}-${x.hex}`}
                            colorDescription={x.description}
                          />
                        ))}
                      </Flex>
                    </FormControl>
                  </Flex>
                )}

                <FormControl isInvalid={!!errors.weight}>
                  <TexasFormLabel>{t('composition.weight')}</TexasFormLabel>
                  <InputGroup>
                    <Input
                      variant="texas"
                      {...register('weight', {
                        setValueAs: (value) =>
                          setValueAsDecimalExtender(value, false),
                        maxLength: {
                          value: 21,
                          message: t('errors.maxLength', { count: 21 }),
                        },
                        min: {
                          value: 0,
                          message: t('composition.canNotBeNegative'),
                        },
                      })}
                      placeholder="0"
                    />
                    <InputRightAddon h="auto" border="none">
                      {t('composition.kg')}
                    </InputRightAddon>
                  </InputGroup>

                  <ErrorLabel text={errors.weight?.message} />
                </FormControl>
                <FormControl isInvalid={!!errors.note}>
                  <TexasFormLabel>{t('general.note')}</TexasFormLabel>
                  <AutoGrowTextareaContainer value={watch('note')}>
                    <Textarea
                      {...register('note')}
                      minHeight={10}
                      height="auto"
                    />
                  </AutoGrowTextareaContainer>
                  <ErrorLabel text={errors.note?.message} />
                </FormControl>
                {isReady && (
                  <SimpleNote
                    py={2}
                    color="yellow.400"
                    _light={{ color: 'orange.500' }}
                  >
                    <Text>
                      {compositionMaterial
                        ? t('materials.updateAndRevertIsReady')
                        : t('materials.addAndRevertIsReady')}
                    </Text>
                  </SimpleNote>
                )}
                <SubmitButton loading={isSubmitting}>
                  {t('general.save')}
                </SubmitButton>
              </Flex>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

function GetGroupedQualitiesOptions(
  qualities: QualityListItem[] | null,
  productGroupId: number | null,
): GroupBase<ReactSelectOption>[] {
  const { t } = useTranslation();
  const recommended = qualities?.filter((q) =>
    q.productGroups.find((pg) => pg.id === productGroupId),
  );

  if (recommended?.length === 0)
    return [
      {
        options:
          qualities?.map<ReactSelectOption>((x) => ({
            label: x.name,
            value: x.id,
          })) ?? [],
      },
    ];

  return [
    {
      label: t('general.recommended'),
      options:
        recommended?.map<ReactSelectOption>((x) => ({
          label: x.name,
          value: x.id,
        })) ?? [],
    },
    {
      label: t('general.others'),
      options:
        qualities
          ?.filter((q) => !recommended?.find((r) => r.id === q.id))
          .map<ReactSelectOption>((x) => ({
            label: x.name,
            value: x.id,
          })) ?? [],
    },
  ];
}

function GetGroupedTreatmentOptions(
  treatments: TreatmentListItem[] | null,
  productGroupId: number | null,
  ref: React.MutableRefObject<HTMLElement | null>,
) {
  const { t } = useTranslation();
  const recommended = treatments?.filter((t) =>
    t.productGroups.find((pg) => pg.id === productGroupId),
  );

  if (recommended?.length === 0)
    return [
      {
        options:
          treatments?.map<ReactSelectOptionWithDesc>((x) => ({
            value: x.id,
            label: x.name,
            description: x.description,
            ref: ref,
          })) ?? [],
      },
    ];

  return [
    {
      label: t('general.recommended'),
      options:
        recommended?.map<ReactSelectOptionWithDesc>((x) => ({
          value: x.id,
          label: x.name,
          description: x.description,
          ref: ref,
        })) ?? [],
    },
    {
      label: t('general.others'),
      options:
        treatments
          ?.filter((t) => !recommended?.find((r) => r.id === t.id))
          .map<ReactSelectOptionWithDesc>((x) => ({
            value: x.id,
            label: x.name,
            description: x.description,
            ref: ref,
          })) ?? [],
    },
  ];
}
